// VER-218: Users admin screen (Step 11.2 list + 11.3/11.4 management drawer).
// Owns the step-up modal (shared by every sensitive action in the drawer) and the
// list refresh after a mutation.
import { useEffect, useState, useCallback, useMemo } from "react";
import { App, Card, Table, Tag, Button, Typography, Badge, Input, Space, Tabs } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { usersApi } from "../../console/consoleApi";
import { useSession } from "../../console/useSession";
import { useStepUp } from "../../console/useStepUp";
import {
  errorMessage,
  roleLabel,
  roleCategory,
  ROLES,
  STATUS_COLORS,
} from "../../console/consoleUtils";
import UserDetailDrawer from "./UserDetailDrawer";

const { Title } = Typography;

// `accountId` (optional): when set, this is the drill-in view of ONE company
// (internal admin, inside Accounts) — a single scoped list, no External/Internal tabs.
// When omitted, it's the account admin's own-org view (session-scoped).
export default function ConsoleUsers({ accountId } = {}) {
  const { message } = App.useApp();
  const { user: actor } = useSession();
  const { withStepUp, modal } = useStepUp();
  const isInternalAdmin = actor?.role === ROLES.INTERNAL_ADMIN;
  // Tabs are only meaningful in the internal admin's global view; a drilled-in company
  // is shown as a single list.
  const showTabs = isInternalAdmin && accountId == null;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null); // user open in the drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersApi.list(accountId ? { accountId } : undefined);
      setUsers(res.data.data.users);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message, accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = (id) => {
    setActiveId(id);
    setDrawerOpen(true);
  };

  // Client-side filter over the loaded rows (email / name / role).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.email, u.name, u.role].some((f) => (f || "").toLowerCase().includes(q))
    );
  }, [users, query]);

  // Split into the two on-screen tables by role category.
  const externalUsers = useMemo(
    () => filtered.filter((u) => roleCategory(u.role) === "external"),
    [filtered]
  );
  const internalUsers = useMemo(
    () => filtered.filter((u) => roleCategory(u.role) === "internal"),
    [filtered]
  );

  const columns = [
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Name", dataIndex: "name", key: "name", render: (n) => n || "—" },
    { title: "Role", dataIndex: "role", key: "role", render: (r) => <Tag>{roleLabel(r)}</Tag> },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: "Preprod-only",
      dataIndex: "isPreprodOnly",
      key: "isPreprodOnly",
      align: "center",
      render: (v) =>
        v ? <Badge status="warning" text="Yes" /> : <Badge status="default" text="No" />,
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, row) => (
        <Button size="small" onClick={() => openDetail(row.id)}>
          Manage
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          Users
        </Title>
      }
      extra={
        <Space>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search email, name, role"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: 260 }}
          />
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
        </Space>
      }
    >
      {/* Only VerifyU Ops sees the External/Internal split. An account admin manages
          just their own organisation's people, so they get a single plain list —
          external roles only, so an internal user is never surfaced to a client even
          if one happens to sit in their account. */}
      {showTabs ? (
        <Tabs
          items={[
            {
              key: "external",
              label: `External (${externalUsers.length})`,
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  columns={columns}
                  dataSource={externalUsers}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                />
              ),
            },
            {
              key: "internal",
              label: `Internal (${internalUsers.length})`,
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  columns={columns}
                  dataSource={internalUsers}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                />
              ),
            },
          ]}
        />
      ) : (
        // Internal admin drilling into a company sees ALL its people (a company like
        // Ops holds internal-role users too). An account admin sees external only, so a
        // stray internal user is never surfaced to a client.
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={isInternalAdmin ? filtered : externalUsers}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      )}

      <UserDetailDrawer
        userId={activeId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onChanged={load}
        withStepUp={withStepUp}
        actorRole={actor?.role}
      />

      {/* Step-up (re-OTP) modal — driven by useStepUp, shared across drawer actions */}
      {modal}
    </Card>
  );
}
